import { useState } from "react";
import { toast } from "sonner";
import { ArrowUpRight, Download, Loader2, Mail, MapPin, Send } from "lucide-react";
import { CONTACT } from "../data/content";
import { Reveal, SectionHeader } from "./ui-bits";

const SUBJECTS = ["WEBSITE DESIGN", "GRAPHIC DESIGN", "SEO & MARKETING", "SOMETHING ELSE"];
const inputCls = "w-full border border-white/15 bg-ink/70 px-4 py-3.5 text-sm text-white placeholder:text-slate-600 outline-none transition-colors focus:border-ember";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: SUBJECTS[0], message: "" });
  const [status, setStatus] = useState("idle");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (status === "sending") return;
    setStatus("sending");
    // hidden honeypot — bots fill it, humans never see it
    if ((e.target.elements._honey?.value || "").trim()) {
      setStatus("sent");
      return;
    }
    try {
      const res = await fetch(`https://formsubmit.co/ajax/${CONTACT.email}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          Name: form.name,
          Email: form.email,
          "Expedition Type": form.subject,
          Message: form.message,
          _subject: `New dispatch from ${form.name} — ${form.subject}`,
          _replyto: form.email,
          _captcha: "false",
          _honey: "",
        }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok || !data || data.success === false || data.success === "false") {
        if (data?.message && String(data.message).toLowerCase().includes("activat")) {
          toast.info("Form service needs a one-time activation — open the owner's email inbox and click the FormSubmit 'Activate Form' link (check Spam too).", { description: "SETUP STEP — ONE TIME ONLY" });
          setStatus("idle");
          return;
        }
        throw new Error("send failed");
      }
      setStatus("sent");
      setForm({ name: "", email: "", subject: SUBJECTS[0], message: "" });
      toast.success("Dispatch received loud and clear — I'll radio back soon.", { description: "MESSAGE LOGGED AT BASE CAMP" });
    } catch {
      setStatus("error");
      toast.error("Radio failed — dispatch didn't get through. Try again.");
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative scroll-mt-20 overflow-hidden py-24 md:py-32">
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-64 bg-[radial-gradient(ellipse_at_50%_120%,rgba(255,107,53,0.14),transparent_65%)]" />
      <div className="relative mx-auto max-w-7xl px-5 md:px-8">
        <SectionHeader
          index="10"
          station="Send A Message"
          id="COMMS // OPEN CHANNEL"
          title={<>Start an expedition<br /><span className="text-ember">with me.</span></>}
          sub="Have an idea that needs a summit push? Send a dispatch from your base camp — I radio back fast."
        />

        <div className="grid items-stretch gap-6 lg:grid-cols-[1fr_1.35fr]">
          {/* direct coordinates — details box */}
          <Reveal delay={0.1} className="h-full">
            <div className="clip-tag flex h-full flex-col border border-white/10 bg-ink/70 p-7 md:p-8" data-testid="contact-details-panel">
              <p className="font-mono text-[10px] tracking-[0.25em] text-ember">DIRECT COORDINATES</p>
              <div className="mt-8 space-y-6">
                <div className="flex items-start gap-4">
                  <Mail className="mt-0.5 h-5 w-5 shrink-0 text-ember" />
                  <div>
                    <p className="font-mono text-[9px] tracking-[0.25em] text-slate-500">EMAIL</p>
                    <a href={`mailto:${CONTACT.email}`} data-testid="contact-email-link" className="text-sm text-white transition-colors hover:text-ember">
                      {CONTACT.email}
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-ember" />
                  <div>
                    <p className="font-mono text-[9px] tracking-[0.25em] text-slate-500">HOME ROUTES</p>
                    <p className="text-sm text-white">Uttarakhand &amp; Himachal Pradesh — Himalaya</p>
                  </div>
                </div>
                <div>
                  <p className="font-mono text-[9px] tracking-[0.25em] text-slate-500">EXPEDITION DOCS</p>
                  <a
                    href={CONTACT.cvUrl}
                    download
                    data-testid="cv-download-button"
                    className="mt-3 flex items-center justify-between border border-ember/40 bg-ember/10 px-4 py-3 font-mono text-[10px] font-bold tracking-[0.2em] text-ember transition-colors hover:bg-ember hover:text-night"
                  >
                    DOWNLOAD CV (PDF)<Download className="h-4 w-4" />
                  </a>
                </div>
                <div>
                  <p className="font-mono text-[9px] tracking-[0.25em] text-slate-500">SOCIAL CHANNELS</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {CONTACT.socials.map((s) => (
                      <a
                        key={s.label}
                        href={s.url}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`contact-social-${s.label.toLowerCase()}`}
                        className="flex items-center gap-1.5 border border-white/15 px-3 py-2 font-mono text-[10px] tracking-[0.18em] text-slate-300 transition-colors hover:border-ember hover:text-ember"
                      >
                        {s.label.toUpperCase()}<ArrowUpRight className="h-3 w-3" />
                      </a>
                    ))}
                  </div>
                </div>
              </div>
              <div className="mt-auto border-t border-dashed border-white/15 pt-5 font-mono text-[9px] leading-relaxed tracking-[0.2em] text-slate-500">
                28.5706° N / 77.3272° E<br />NOIDA, INDIA — GMT +5:30
              </div>
            </div>
          </Reveal>

          {/* dispatch form */}
          <Reveal delay={0.15} className="h-full">
            <form onSubmit={submit} data-testid="contact-form" className="clip-tag relative flex h-full flex-col border border-white/10 bg-ink/70 p-6 md:p-8">
              <input name="_honey" tabIndex={-1} autoComplete="off" aria-hidden="true" className="pointer-events-none absolute -left-[9999px] h-px w-px overflow-hidden opacity-0" />
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="cf-name" className="mb-2 block font-mono text-[9px] tracking-[0.25em] text-slate-400">CLIMBER NAME *</label>
                  <input id="cf-name" required value={form.name} onChange={set("name")} data-testid="contact-input-name" placeholder="Your name" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="cf-email" className="mb-2 block font-mono text-[9px] tracking-[0.25em] text-slate-400">EMAIL *</label>
                  <input id="cf-email" type="email" required value={form.email} onChange={set("email")} data-testid="contact-input-email" placeholder="you@basecamp.com" className={inputCls} />
                </div>
              </div>

              <div className="mt-5">
                <label className="mb-2 block font-mono text-[9px] tracking-[0.25em] text-slate-400">EXPEDITION TYPE</label>
                <div className="flex flex-wrap gap-2">
                  {SUBJECTS.map((s) => (
                    <button
                      type="button"
                      key={s}
                      onClick={() => setForm((f) => ({ ...f, subject: s }))}
                      data-testid={`contact-subject-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      className={`px-3 py-2 font-mono text-[9px] tracking-[0.2em] transition-colors ${form.subject === s ? "bg-ember font-bold text-night" : "btn-fill"}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-5 flex min-h-0 flex-1 flex-col">
                <label htmlFor="cf-message" className="mb-2 block font-mono text-[9px] tracking-[0.25em] text-slate-400">DISPATCH DETAILS *</label>
                <textarea id="cf-message" required rows={5} value={form.message} onChange={set("message")} data-testid="contact-input-message" placeholder="Tell me about the peak we're taking on…" className={`${inputCls} flex-1 resize-none`} />
              </div>

              <div className="mt-auto pt-6">
                <button
                  type="submit"
                  data-testid="contact-form-submit-button"
                  disabled={status === "sending"}
                  className="btn-primary flex w-full items-center justify-center gap-3 px-8 py-4 font-mono text-[11px] font-bold tracking-[0.22em] disabled:opacity-60"
                >
                  {status === "sending" ? (
                    <><Loader2 className="h-4 w-4 animate-spin" />TRANSMITTING…</>
                  ) : (
                    <><Send className="h-4 w-4" />TRANSMIT DISPATCH</>
                  )}
                </button>
                {status === "sent" && (
                  <p data-testid="contact-success-note" className="mt-4 font-mono text-[10px] tracking-[0.2em] text-glacier">
                    SIGNAL RECEIVED — LOGGED AT BASE CAMP
                  </p>
                )}
              </div>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
